import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Tag, X } from 'lucide-react';
import { ApiError, api } from '../lib/api.js';
import { useToast } from '../ui/index.js';

/**
 * A ticket's tags: shown, added and removed here, and each one a link to the
 * queue filtered by it. Tags could be added in bulk from the queue and were
 * then shown nowhere, which made them look like they did nothing.
 */
export function TicketTags({
  tenant,
  number,
  tags,
  canEdit,
}: {
  tenant: string;
  number: number;
  tags: string[];
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const save = useMutation({
    mutationFn: (next: string[]) => api.patch(`/api/v1/tenants/${tenant}/tickets/${number}`, { tags: next }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ticket', tenant, String(number)] });
      void qc.invalidateQueries({ queryKey: ['tickets'] });
      setDraft('');
      setAdding(false);
    },
    onError: (e) => toast.error('Tags not saved', e instanceof ApiError ? e.message : 'the change failed'),
  });

  if (tags.length === 0 && !canEdit) return null;
  const add = () => {
    const t = draft.trim().slice(0, 40);
    if (!t || tags.includes(t)) return setAdding(false);
    save.mutate([...tags, t]);
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      <Tag aria-hidden className="h-3 w-3 text-ink-tertiary" />
      {tags.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 rounded-full border border-line bg-surface px-2 py-0.5 text-2xs text-ink-secondary">
          <Link to={`/queue?tag=${encodeURIComponent(t)}`} className="hover:text-ink" title={`Tickets tagged “${t}”`}>
            {t}
          </Link>
          {canEdit && (
            <button
              type="button"
              aria-label={`Remove tag ${t}`}
              className="text-ink-tertiary hover:text-ink"
              disabled={save.isPending}
              onClick={() => save.mutate(tags.filter((x) => x !== t))}
            >
              <X aria-hidden className="h-2.5 w-2.5" />
            </button>
          )}
        </span>
      ))}
      {canEdit &&
        (adding ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') add();
              if (e.key === 'Escape') setAdding(false);
            }}
            onBlur={add}
            maxLength={40}
            placeholder="tag"
            aria-label="New tag"
            className="h-6 w-28 rounded-full border border-line bg-surface px-2 text-2xs text-ink outline-none focus:border-brand"
          />
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-line px-2 py-0.5 text-2xs text-ink-tertiary hover:border-brand hover:text-ink"
          >
            <Plus aria-hidden className="h-2.5 w-2.5" />
            Tag
          </button>
        ))}
    </div>
  );
}
